import { RefreshCw, ChevronLeft } from 'lucide-react';
import { useState, useEffect } from 'react';
import { StatusDisplay } from './StatusDisplay';
import { useInterval } from '~/lib/hooks/useInterval';
import { OtpInput } from './otp-input';
import IconsMapping from '~/components/ui/Icons/IconsMapping';

interface EmailOnboardingProps {
  email: string;
  handleBackToLogin: () => void;
  isInitialSending?: boolean;
  setSuccess: (success: boolean) => void;
  success: boolean;
}

// Email masking function
const maskEmail = (email: string) => {
  const [localPart, domain] = email.split('@');

  if (!localPart || !domain) {
    return email;
  }

  if (localPart.length <= 2) {
    return email;
  }

  return `${localPart.substring(0, 2)}${'*'.repeat(6)}.com`;
};

export default function EmailOnboarding({
  email,
  handleBackToLogin,
  isInitialSending = false,
  setSuccess,
  success,
}: EmailOnboardingProps) {
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [canResend, setCanResend] = useState(false);
  const [countdown, setCountdown] = useState(30);

  // Countdown timer
  useInterval(
    () => {
      if (countdown > 0) {
        setCountdown(countdown - 1);
      } else {
        setCanResend(true);
      }
    },
    !canResend ? 1000 : null, // Stop interval when canResend is true
  );

  const handleSendOTP = async () => {
    console.log('[EmailOnboarding] === Starting OTP Send Process ===');
    console.log('[EmailOnboarding] Email:', email);

    setIsLoading(true);
    setError('');

    try {
      console.log('[EmailOnboarding] Sending OTP request to API...');

      const response = await fetch('/api/otp/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      console.log('[EmailOnboarding] OTP send response status:', response.status);

      const data = (await response.json()) as { error?: string; message?: string };
      console.log('[EmailOnboarding] OTP send response data:', data);

      if (!response.ok) {
        console.error('[EmailOnboarding] OTP send failed:', data.error);
        setError(data.error || 'Failed to send OTP');
        return;
      }

      console.log('[EmailOnboarding] OTP sent successfully');
      setSuccess(true);
    } catch (error) {
      console.error('[EmailOnboarding] === Error in OTP Send Process ===');
      console.error('[EmailOnboarding] Error type:', error instanceof Error ? error.constructor.name : typeof error);
      console.error('[EmailOnboarding] Error message:', error instanceof Error ? error.message : error);
      console.error('[EmailOnboarding] Error stack:', error instanceof Error ? error.stack : 'No stack trace');

      setError('Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (otpValue?: string) => {
    const otpToVerify = otpValue || otp;

    console.log('[EmailOnboarding] === Starting OTP Verify Process ===');
    console.log('[EmailOnboarding] Email:', email);
    console.log('[EmailOnboarding] OTP:', otpToVerify);
    setSuccess(false);

    if (!otpToVerify || otpToVerify.length !== 6) {
      console.error('[EmailOnboarding] Invalid OTP length:', otpToVerify.length);
      setError('Please enter a 6-digit OTP');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      console.log('[EmailOnboarding] Sending OTP verification request to API...');

      const response = await fetch('/api/otp/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, otp: otpToVerify }),
      });

      console.log('[EmailOnboarding] OTP verify response status:', response.status);

      const data = (await response.json()) as { error?: string; user?: any; token?: string };
      console.log('[EmailOnboarding] OTP verify response data:', data);

      if (!response.ok) {
        console.error('[EmailOnboarding] OTP verification failed:', data.error);

        // Check for specific OTP expired message and provide a more user-friendly response
        if (data.error && data.error.toLowerCase().includes('otp expired or not found')) {
          setError('OTP expired. Please request a new code.');
        } else {
          setError(data.error || 'Failed to verify OTP');
        }

        setSuccess(false);
        return;
      }

      console.log('[EmailOnboarding] OTP verification successful');
      console.log('[EmailOnboarding] User data:', data.user);
      console.log('[EmailOnboarding] Token:', data.token ? 'Present' : 'Missing');

      // Redirect to home page after successful verification
      window.location.href = '/check-activation';
    } catch (error) {
      console.error('[EmailOnboarding] === Error in OTP Verify Process ===');
      console.error('[EmailOnboarding] Error type:', error instanceof Error ? error.constructor.name : typeof error);
      console.error('[EmailOnboarding] Error message:', error instanceof Error ? error.message : error);
      console.error('[EmailOnboarding] Error stack:', error instanceof Error ? error.stack : 'No stack trace');

      setError('Failed to verify OTP. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOTP = () => {
    setCanResend(false);
    setCountdown(30); // Reset countdown
    console.log('[EmailOnboarding] Resending OTP...');
    handleSendOTP();
  };

  useEffect(() => {
    if (!canResend) {
      const timer = setTimeout(() => setCanResend(true), 300000); // 5 minutes
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [canResend]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(false), 2000);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [success, setSuccess]);

  return (
    <div className="w-full max-w-md mx-auto font-inter-display px-4">
      <div className="flex flex-col gap-4 sm:gap-4">
        {/* Header */}
        <div className="flex flex-col gap-1">
          <h1 className="text-[#FDFDFD] text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-medium leading-[1.2em] mb-2">
            Verify your email
          </h1>

          <div className="text-[#696A71] text-base sm:text-md lg:text-md font-normal leading-[1.2em]">
            <p>We've sent a 6-digit code to {maskEmail(email)}</p>
            <p>Enter it below to continue.</p>
          </div>
        </div>

        {/* Form */}
        <div className="flex flex-col gap-1">
          <div className="flex flex-col gap-5">
            {/* OTP Input Component */}
            <div className="flex justify-between py-4">
              <OtpInput
                value={otp}
                onChange={(value: string) => {
                  setOtp(value);
                  setError('');
                }}
                maxLength={6}
                disabled={isLoading || isInitialSending || isVerifying}
                onComplete={(value) => handleVerifyOTP(value)}
              />
            </div>
          </div>

          {/* Status Messages */}
          <div className="space-y-3">
            {error && <StatusDisplay status="failed" message={error} title="Verification Failed" />}
            {success && (
              <StatusDisplay status="success" message="OTP sent successfully! Check your email." title="OTP Sent" />
            )}
            {isInitialSending && (
              <StatusDisplay status="loading" message="Sending OTP to your email..." title="Sending OTP" />
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            <button
              type="button"
              onClick={() => handleVerifyOTP()}
              disabled={!otp || otp.length !== 6 || isVerifying || isInitialSending}
              className={`w-full rounded-xl py-3 sm:py-2.5 px-4 shadow-sm transition-colors text-black min-h-[48px] sm:min-h-auto ${
                otp && otp.length === 6 && !isVerifying && !isInitialSending
                  ? 'bg-[#F7F7F7] hover:bg-[#E5E5E5]'
                  : 'bg-[#CCCCCC] cursor-not-allowed'
              }`}
            >
              {isVerifying ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-black border-t-[#CCCCCC] rounded-full animate-spin"></div>
                  <span className="text-base font-medium leading-[1.5em] tracking-[0.02em]">Verifying...</span>
                </div>
              ) : (
                <span className="text-base font-medium leading-[1.5em] tracking-[0.02em]">Continue</span>
              )}
            </button>

            {/* Bottom Navigation */}
            <div className="text-center text-[rgba(255,255,255,0.5)] text-sm font-normal leading-[1.2em] flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
              <button
                type="button"
                className="text-white flex items-center justify-center sm:justify-start gap-1 cursor-pointer min-h-[44px] sm:min-h-auto"
                onClick={handleBackToLogin}
              >
                <div className="i-ph:arrow-left w-4 h-4" />
                <span>Use a different Email</span>
              </button>

              <button
                type="button"
                className={`text-[#888888] text-center sm:text-right min-h-[44px] sm:min-h-auto flex items-center justify-center sm:justify-end gap-1 ${
                  canResend ? 'cursor-pointer text-white hover:text-white' : 'opacity-50 cursor-not-allowed'
                }`}
                onClick={canResend ? handleResendOTP : undefined}
                disabled={!canResend}
              >
                {IconsMapping.refresh}
                <span>Resend code {countdown > 0 ? `in ${countdown}s` : ''}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
